import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fmtDateShort } from '../utils/dates';
import { PRIORITY_AR } from '../utils/labels';

const COLUMNS = [
  { key: 'pending',          label: 'جديدة',          color: 'var(--text-faint)' },
  { key: 'in_progress',      label: 'قيد العمل',       color: 'var(--c-blue-500)' },
  { key: 'waiting_approval', label: 'بانتظار اعتماد', color: 'var(--c-warning)' },
  { key: 'revision',         label: 'مراجعة',          color: 'var(--c-coral)' },
  { key: 'completed',        label: 'مكتملة',          color: 'var(--c-success)' },
];

const PRIORITY_BG = { urgent: 'var(--c-coral-soft)', high: 'var(--c-gold-soft)', medium: 'var(--c-blue-100)', low: 'var(--surface-alt)' };
const PRIORITY_COLOR = { urgent: 'var(--c-danger)', high: 'var(--c-gold-ink)', medium: 'var(--text-strong)', low: 'var(--text-muted)' };

export default function TasksKanban({ tasks, onStatusChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dragging, setDragging] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.key] = tasks.filter(t => t.status === c.key);
    return acc;
  }, {});

  const handleDrop = (status) => {
    if (dragging && dragging.status !== status) {
      onStatusChange(dragging.id, status);
    }
    setDragging(null);
    setHoverCol(null);
  };

  const canDrag = ['admin', 'team'].includes(user?.role);

  return (
    <div className="kanban-board">
      {COLUMNS.map(col => (
        <div
          key={col.key}
          className={`kanban-col ${hoverCol === col.key ? 'drop-hover' : ''}`}
          onDragOver={e => { e.preventDefault(); setHoverCol(col.key); }}
          onDragLeave={() => setHoverCol(null)}
          onDrop={() => handleDrop(col.key)}
        >
          <div className="kanban-col-header" style={{ borderTopColor: col.color }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{col.label}</span>
            <span className="kanban-count">{byStatus[col.key].length}</span>
          </div>
          <div className="kanban-list">
            {byStatus[col.key].length === 0 ? (
              <div className="kanban-empty">لا توجد</div>
            ) : byStatus[col.key].map(t => (
              <div
                key={t.id}
                className="kanban-card"
                draggable={canDrag}
                onDragStart={() => setDragging(t)}
                onDragEnd={() => setDragging(null)}
                onClick={() => navigate(`/tasks/${t.id}`)}
              >
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, lineHeight: 1.4 }}>{t.title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11 }}>
                  <span style={{
                    padding: '2px 6px', borderRadius: 10,
                    background: PRIORITY_BG[t.priority] || 'var(--surface-alt)',
                    color: PRIORITY_COLOR[t.priority] || 'var(--text-muted)'
                  }}>{PRIORITY_AR[t.priority] || t.priority}</span>
                  {t.bucket && <span style={{ color: 'var(--text-muted)' }}>🗂️ {t.bucket}</span>}
                  {t.client?.name && <span style={{ color: 'var(--text-muted)' }}>👤 {t.client.name}</span>}
                  {t.show_checklist_on_card && t.checklist_items_count > 0 && (
                    <span style={{ color: 'var(--text-muted)' }} title="قائمة الاختيار">
                      ☑️ {t.checklist_done_count}/{t.checklist_items_count}
                    </span>
                  )}
                </div>
                {t.show_notes_on_card && t.notes && (
                  <div className="card-notes">{t.notes}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  <span>
                    {(t.assignees || []).length > 1
                      ? `${t.assignees[0].name} +${t.assignees.length - 1}`
                      : (t.assignee?.name || 'غير محدد')}
                  </span>
                  {t.due_date && (
                    <span style={{ color: new Date(t.due_date) < new Date() && t.status !== 'completed' ? 'var(--danger)' : 'inherit' }}>
                      📅 {fmtDateShort(t.due_date)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
